import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock } from 'lucide-react';
import PublicNavbar from '../components/PublicNavbar';
import FooterBackground from '../components/FooterBackground';
import SEOHead from '../components/SEOHead';
import { guideList } from '../data/guides';

export default function GuidesIndex() {
  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="guides-index">
      <SEOHead
        title="Cloud Cost Optimization Guides — AWS, Azure, GCP, Snowflake, Databricks"
        description="Free, in-depth FinOps guides for the 5 platforms that drive 95% of cloud spend. Each guide gives you the exact patterns, diagnostic SQL/CLI, and savings estimates."
        canonical="https://cartieai.com/guides"
        keywords="aws cost optimization guide, azure cost optimization guide, gcp cost optimization, snowflake cost optimization, databricks cost optimization, finops guides"
      />

      <PublicNavbar />

      <section className="relative px-4 pt-24 pb-12 overflow-hidden">
        <FooterBackground />
        <div className="relative max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-emerald-400 uppercase">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span>FinOps Field Guides</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            Cut your cloud bill,{' '}
            <span className="text-emerald-400">
              one platform at a time.
            </span>
          </h1>
          <p className="text-lg text-slate-400 max-w-3xl">
            Five long-form guides — one per platform — with the exact patterns, diagnostic
            queries, and savings estimates we use on every CARTIEAI customer audit. No fluff.
            Bookmark them.
          </p>
          <div className="flex flex-wrap gap-3 mt-8 text-xs text-slate-400">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
              Audit-tested patterns
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
              Copy-paste SQL / CLI
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
              Average 30-45% savings
            </span>
          </div>
        </div>
      </section>

      <section className="px-4 pb-24">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {guideList.map((g) => (
            <Link
              key={g.slug}
              to={`/guides/${g.slug}`}
              data-testid={`guide-card-${g.slug}`}
              className="group relative rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
            >
              <div className={`absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r ${g.accent} opacity-60`} />
              <div className="text-3xl mb-4">{g.icon}</div>
              <div className="text-xs font-bold tracking-widest text-slate-400 uppercase mb-2">
                {g.longName}
              </div>
              <h2 className="text-xl font-bold mb-3 leading-tight group-hover:text-white transition-colors">
                {g.title}
              </h2>
              <p className="text-sm text-slate-400 mb-5 line-clamp-3">{g.intro.slice(0, 160)}…</p>

              <div className="flex flex-wrap gap-2 text-xs mb-5">
                {g.quickStats.map((s) => (
                  <span
                    key={s.label}
                    className="px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-slate-700"
                  >
                    <span className="text-white font-semibold">{s.value}</span>{' '}
                    <span className="text-slate-400">· {s.label}</span>
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-1.5 text-sm font-medium text-emerald-400 group-hover:gap-2.5 transition-all">
                <span>Read the guide</span>
                <ArrowRight className="w-4 h-4" />
              </div>

              <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-400">
                <Clock className="w-3.5 h-3.5" />
                <span>{g.patterns.length} patterns · ~10 min read</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
